import type { AutenticacaoDoRemetente, MensagemRetida } from '@central/contracts';

/**
 * As decisões da triagem que não dependem de DOM.
 *
 * Moram fora do `.tsx` de propósito: o projeto `web` do vitest roda em `node` e
 * só enxerga `.ts`, então o que estiver aqui tem teste e o que estiver no
 * componente não tem. A regra de quando um botão pode ser clicado é
 * exatamente o tipo de coisa que precisa de teste — errada, ela abre um chamado
 * sem solicitante ou descarta um e-mail sem motivo escrito.
 */

export const MODOS = {
  cadastrar: 'NOVO_CONTATO',
  existente: 'SOLICITANTE_EXISTENTE',
} as const;

export type ModoDeLiberacao = (typeof MODOS)[keyof typeof MODOS];

/** Cada decisão possível, e o que ela custa. */
export type Decisao = 'cadastrar' | 'existente' | 'descartar';

export interface RotuloDeAutenticacao {
  texto: string;
  detalhe: string;
  /** `ok` pinta de sucesso, `alerta` de aviso, `neutro` de silêncio. */
  tom: 'ok' | 'alerta' | 'neutro';
}

/**
 * O veredito da autenticação em uma linha.
 *
 * Três estados, e não dois: "não passou" e "não foi verificado" pedem reações
 * opostas. Um DMARC `fail` é motivo para descartar sem ler; a **ausência** dos
 * três resultados é o gateway de e-mail não os ter carimbado, e não diz nada
 * sobre o remetente. Colapsar os dois em "suspeito" ensinaria a operação a
 * ignorar o aviso, que é como um alerta morre.
 */
export function rotularAutenticacao(auth: AutenticacaoDoRemetente): RotuloDeAutenticacao {
  const semCarimbo = !auth.spf && !auth.dkim && !auth.dmarc;
  if (semCarimbo) {
    return {
      texto: 'Não verificada',
      detalhe:
        'A mensagem chegou sem resultado de SPF, DKIM ou DMARC. Isso é do caminho do e-mail, não do remetente — confira o conteúdo antes de decidir.',
      tom: 'neutro',
    };
  }
  if (auth.confiavel) {
    return {
      texto: 'Autenticada',
      detalhe: descreverResultados(auth),
      tom: 'ok',
    };
  }
  return {
    texto: 'Falhou na autenticação',
    detalhe: `${descreverResultados(auth)} — o remetente pode estar falsificando o domínio.`,
    tom: 'alerta',
  };
}

function descreverResultados(auth: AutenticacaoDoRemetente): string {
  const partes = [
    ['SPF', auth.spf],
    ['DKIM', auth.dkim],
    ['DMARC', auth.dmarc],
  ]
    .filter(([, valor]) => Boolean(valor))
    .map(([rotulo, valor]) => `${rotulo}: ${valor}`);
  return partes.join(' · ');
}

/**
 * O nome com que o remetente será cadastrado, quando ninguém digitar um.
 *
 * O nome de exibição do cabeçalho `From` primeiro; o endereço inteiro depois.
 * Nunca só a parte antes do `@`: `contato@` e `ti@` viram contatos chamados
 * "contato" e "ti" na base do cliente, e ninguém consegue mais achá-los.
 */
export function nomeSugerido(mensagem: Pick<MensagemRetida, 'nomeDoRemetente' | 'remetente'>): string {
  const exibicao = mensagem.nomeDoRemetente?.trim();
  return exibicao && exibicao.length > 0 ? exibicao : mensagem.remetente;
}

export interface EstadoDaDecisao {
  decisao: Decisao;
  /** `cadastrar`: a conta que recebe o contato. */
  contaId: string | null;
  nome: string;
  /** `existente`: quem responde pelo chamado. */
  solicitanteId: string | null;
  /** `descartar`: por quê. */
  motivo: string;
}

/**
 * Por que o botão está desligado — ou `null` quando ele pode ser clicado.
 *
 * Devolve o **motivo**, e não um booleano, porque um botão apagado sem
 * explicação é a forma mais cara de dizer "faltou alguma coisa": a pessoa
 * clica, nada acontece, e ela procura o erro no lugar errado.
 */
export function impedimentoDaDecisao(estado: EstadoDaDecisao): string | null {
  switch (estado.decisao) {
    case 'cadastrar':
      if (!estado.contaId) return 'Escolha em qual cliente o remetente será cadastrado.';
      if (estado.nome.trim().length < 2) return 'Informe o nome do contato.';
      return null;
    case 'existente':
      if (!estado.solicitanteId) return 'Escolha em nome de quem o chamado será aberto.';
      return null;
    case 'descartar':
      if (estado.motivo.trim().length < 3) {
        return 'Diga por que está descartando. A mensagem não é apagada, e o motivo é o que explica a decisão depois.';
      }
      return null;
  }
}

/**
 * O que a decisão faz com o **próximo** e-mail do mesmo endereço.
 *
 * É a diferença que a tela precisa dizer antes do clique, não depois: liberar
 * em nome de outra pessoa resolve esta mensagem e deixa a seguinte cair na fila
 * de novo, porque o endereço continua sem dono.
 */
export function consequenciaDaDecisao(estado: EstadoDaDecisao): string {
  switch (estado.decisao) {
    case 'cadastrar':
      return 'O remetente passa a existir no cadastro: o próximo e-mail dele abre chamado sozinho.';
    case 'existente':
      return 'O endereço continua sem cadastro — o próximo e-mail dele será retido de novo.';
    case 'descartar':
      return 'A mensagem sai da fila e continua guardada como prova, com o motivo e o autor do descarte.';
  }
}

/** Quantos dias a mensagem está parada. `null` quando não há data de chegada. */
export function diasParada(recebidaEm: string | null, agora = Date.now()): number | null {
  if (!recebidaEm) return null;
  const chegada = Date.parse(recebidaEm);
  if (Number.isNaN(chegada)) return null;
  return Math.max(Math.floor((agora - chegada) / 86_400_000), 0);
}

/**
 * O texto do aviso no painel inicial.
 *
 * Singular e plural resolvidos aqui, e a idade da fila junto: "3 mensagens
 * retidas" é um número; "a mais antiga há 6 dias" é o que faz alguém abrir.
 */
export function textoDoAviso(retidas: number, maisAntigaEm: string | null, agora = Date.now()): string {
  const dias = diasParada(maisAntigaEm, agora);
  const contagem =
    retidas === 1 ? '1 mensagem esperando decisão' : `${retidas} mensagens esperando decisão`;
  if (dias === null) return contagem;
  if (dias === 0) return `${contagem} — a mais antiga chegou hoje`;
  if (dias === 1) return `${contagem} — a mais antiga está parada há 1 dia`;
  return `${contagem} — a mais antiga está parada há ${dias} dias`;
}
